import React from 'react';
import { useTranslation } from '@/contexts/LanguageContext';
import { CarouselStyle, FontStyle, Theme } from '@/types';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { Sun, Moon } from 'lucide-react';

interface StylePanelProps {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  style: CarouselStyle;
  onConvertStyle: (style: CarouselStyle) => void;
  onStyleChange?: (style: CarouselStyle) => void;
  fontStyle: FontStyle;
  setFontStyle: (style: FontStyle) => void;
  showAccent: boolean;
  setShowAccent: (show: boolean) => void;
  accentColor: string;
  setAccentColor: (color: string) => void;
  styleOptions: Array<{ value: CarouselStyle; label: string }>;
  fontOptions: Array<{ value: FontStyle; label: string }>;
}

const StylePanel: React.FC<StylePanelProps> = ({
  theme,
  setTheme,
  style,
  onConvertStyle,
  onStyleChange,
  fontStyle,
  setFontStyle,
  showAccent,
  setShowAccent,
  accentColor,
  setAccentColor,
  styleOptions,
  fontOptions,
}) => {
  const { t } = useTranslation();

  return (
    <div className="p-3 space-y-4">
      {/* Theme toggle */}
      <div data-tour="mobile-theme-toggle" className="flex items-center justify-between">
        <Label className="text-sm font-medium">{t('workspace.theme')}</Label>
        <div className="flex bg-secondary rounded-lg p-1">
          <Button variant="ghost" size="sm" onClick={() => setTheme('LIGHT')} className={cn("px-3 py-1 text-xs font-bold", theme === 'LIGHT' ? 'bg-white text-black shadow' : 'text-muted-foreground hover:text-foreground')}>
            <Sun className="h-3 w-3 mr-1" /> {t('workspace.light')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setTheme('DARK')} className={cn("px-3 py-1 text-xs font-bold", theme === 'DARK' ? 'bg-black text-white shadow' : 'text-muted-foreground hover:text-foreground')}>
            <Moon className="h-3 w-3 mr-1" /> {t('workspace.dark')}
          </Button>
        </div>
      </div>

      {/* Style selector */}
      {onStyleChange && (
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">{t('workspace.style')}</Label>
          <Select value={style} onValueChange={(value) => onConvertStyle(value as CarouselStyle)}>
            <SelectTrigger className="w-[140px] h-8"><SelectValue /></SelectTrigger>
            <SelectContent>
              {styleOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Font style */}
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{t('workspace.fontStyle')}</Label>
        <Select value={fontStyle} onValueChange={(value) => setFontStyle(value as FontStyle)}>
          <SelectTrigger className="w-[140px] h-8"><SelectValue /></SelectTrigger>
          <SelectContent>
            {fontOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Accent color */}
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{t('workspace.accentColor')}</Label>
        <Switch checked={showAccent} onCheckedChange={setShowAccent} />
      </div>
      {showAccent && (
        <div className="flex items-center gap-3 bg-secondary p-2 rounded-lg border border-border">
          <input type="color" value={accentColor} onChange={(e) => setAccentColor(e.target.value)} className="w-8 h-8 rounded cursor-pointer border-0 p-0 bg-transparent" />
          <Input type="text" value={accentColor} onChange={(e) => setAccentColor(e.target.value)} className="flex-1 font-mono uppercase" maxLength={7} placeholder="#RRGGBB" />
        </div>
      )}
    </div>
  );
};

export default StylePanel;
